/**
 * The EDDY brand mark — three stacked bars whose short middle arm is brand
 * blue, so the stack reads as an **E**.
 *
 * Geometry is verbatim from `Eddy Design Files/design_handoff_sidebar_header`
 * §3. Two components, ONE geometry:
 *
 *   - `EddyMark` — bars only. For places too small or too narrow for the
 *     lockup (the collapsed rail, favicons, empty states).
 *   - `EddyWordmark` — the same three bars plus "DDY", in a SINGLE svg. That
 *     is deliberate and load-bearing: composing it from an svg next to a live
 *     heading lets the E→DDY docking gap and the letter-spacing drift apart
 *     between browsers and font-load states, and the lockup stops looking
 *     like one drawing.
 *
 * Two colour rules from the handoff, both enforced by using vars here:
 * the ink is `--ink-0`, so the mark inverts for free in dark theme, and the
 * blue arm is always `--eddy-blue` — the one fixed colour in the mark. It is
 * never tinted to match a state and the lockup is never monochromed.
 *
 * `size` is the rendered height of the 45-unit BAR STACK, not the height of
 * the svg box — so a mark and a wordmark asked for the same size line up.
 */

import type { CSSProperties } from 'react';
import { Tip } from '~/components/ui/tip';
import { cn } from '~/lib/utils';

/** Bar-stack geometry, shared by both marks so they can never disagree. */
const BARS = [
  { y: 22, w: 42, fill: 'var(--ink-0)' },
  { y: 40, w: 25, fill: 'var(--eddy-blue)' }, // the blue arm
  { y: 58, w: 42, fill: 'var(--ink-0)' },
] as const;

function Bars() {
  return (
    <>
      {BARS.map((b) => (
        <rect key={b.y} x={16} y={b.y} width={b.w} height={9} rx={2} fill={b.fill} />
      ))}
    </>
  );
}

export function EddyMark({ size = 22, className }: { size?: number; className?: string }) {
  return (
    <svg
      viewBox="14 19 46 51"
      width={size}
      height={(size * 51) / 46}
      className={className}
      style={{ display: 'block' }}
      aria-hidden="true"
      focusable="false"
    >
      <Bars />
    </svg>
  );
}

export function EddyWordmark({ size = 22, className }: { size?: number; className?: string }) {
  // The bar stack spans y22→y67 = 45 units of an 88-unit box, and the box is
  // 210 wide — so asking for a 13px stack gives a 25.4 × 60.7px lockup.
  const height = (size * 88) / 45;
  const width = (height * 210) / 88;
  return (
    <svg
      viewBox="0 0 210 88"
      width={width}
      height={height}
      className={className}
      // Kills the inline-baseline drift an svg otherwise picks up from the
      // surrounding line box.
      style={{ display: 'block' }}
      role="img"
      aria-label="EDDY"
    >
      <Bars />
      <text
        x={68}
        y={67}
        // The app's own sans token — Geist, loaded as a variable font at boot
        // (`main.tsx`), so weight 700 is available before first paint and the
        // wordmark does not reflow.
        fontFamily="var(--font-sans)"
        fontWeight={700}
        fontSize={62}
        letterSpacing={0.5}
        fill="var(--ink-0)"
      >
        DDY
      </text>
    </svg>
  );
}

/**
 * The loading indicator: the same E, its bars drawing in one after another
 * (`.eddy-loading-bar` in `app.css`; still under reduced motion). It is the
 * `status` element itself, so a caller needs no extra live region.
 */
export function EddyLoadingMark({ size = 22, className }: { size?: number; className?: string }) {
  return (
    <svg
      viewBox="14 19 46 51"
      width={size}
      height={(size * 51) / 46}
      className={cn('shrink-0', className)}
      style={{ display: 'block' }}
      role="status"
      aria-label="Loading"
      data-testid="eddy-loading-mark"
    >
      {BARS.map((b, i) => (
        <rect
          key={b.y}
          className="eddy-loading-bar"
          style={{ '--eddy-loading-delay': `${i * 140}ms` } as CSSProperties}
          x={16}
          y={b.y}
          width={b.w}
          height={9}
          rx={2}
          fill={b.fill}
        />
      ))}
    </svg>
  );
}

/**
 * EDDY's avatar in a chat answer — the brand mark on a small neutral tile,
 * the same footprint (24px, rounded-md) as the user's initials tile opposite
 * it. The tile is neutral rather than `bg-primary` on purpose: primary is
 * blue, and the mark's blue arm would vanish against it (see the colour
 * rules above — the arm is never tinted to suit a background).
 */
export function EddyAvatar({ className }: { className?: string }) {
  return (
    <Tip label="EDDY">
      <div
        data-slot="avatar"
        data-testid="eddy-avatar"
        role="img"
        aria-label="EDDY"
        className={cn(
          'inline-flex h-6 w-6 shrink-0 select-none items-center justify-center rounded-md bg-card ring-1 ring-border/60',
          className,
        )}
      >
        <EddyMark size={12} />
      </div>
    </Tip>
  );
}

/**
 * EDDY's app icon: white bars on an `--eddy-blue` tile with a 14/64 corner.
 * The middle arm is white at 62% opacity, because blue on blue would vanish
 * (the brand rule, EDDY assets README). Decoration beside a name, so hidden
 * from assistive tech.
 */
export function EddyAppIcon({ size = 32, className }: { size?: number; className?: string }) {
  return (
    <svg
      viewBox="0 0 64 64"
      width={size}
      height={size}
      className={className}
      style={{ display: 'block' }}
      aria-hidden="true"
      focusable="false"
    >
      <rect width={64} height={64} rx={14} fill="var(--eddy-blue)" />
      <rect x={19} y={18} width={26} height={5.6} rx={1.3} fill="#fff" />
      <rect x={19} y={29.2} width={15.5} height={5.6} rx={1.3} fill="#fff" fillOpacity={0.62} />
      <rect x={19} y={40.4} width={26} height={5.6} rx={1.3} fill="#fff" />
    </svg>
  );
}
